/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Component } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State } from 'app/main/store';
import {
  SelectTenantDetails,
  ShowTenantCreationWizard
} from 'app/page-modules/tenant-management/store/tenant-management.actions';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenantDetails, AuditRequestReason } from 'app/page-modules/tenant-management/models';
import * as fromTenants from 'app/page-modules/tenant-management/store/tenant-management.selectors';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { map } from 'rxjs/operators';
import { filterUndefined, observableCombineLatest } from 'app/shared/rxjs/rxjs.utils';
import * as fromShared from 'app/user-auth/store/user-auth.selectors';
import * as fromSharedData from 'app/shared-data/store/shared-data.selectors';
import { AuthService } from 'app/user-auth/services/auth.service';
import { LoginToTenant, AuditedLoginToTenant } from 'app/user-auth/store/user-auth.actions';
import { UserDetails } from 'app/user-auth/models';
import { MatDialog } from '@angular/material/dialog';
import { TenantLoginReasonPanelComponent } from 'app/shared/components/tenant-login-reason-panel/tenant-login-reason-panel.component';

@Component({
  selector: 'og-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrls: ['./tenant-list.component.scss'],
  standalone: false
})
export class TenantListComponent {
  tenants$: Observable<TenantDetails[]>;
  provisioningTenants$: Observable<TenantDetails[]>;
  enabledTenants$: Observable<TenantDetails[]>;
  disabledTenants$: Observable<TenantDetails[]>;
  userDetails$: Observable<UserDetails>;

  tenantListLoading$: Observable<boolean>;
  tenantFilter$ = new BehaviorSubject<string>('');
  showCreateTenantWizard$: Observable<boolean>;

  constructor(
    private store: Store<State>,
    private router: Router,
    private authService: AuthService,
    private matDialog: MatDialog
  ) {
    this.tenants$ = store.pipe(
      select(fromSharedData.getTenantsData),
      filterUndefined(),
      map(tenants => _.sortBy(tenants, tenant => tenant.organizationId))
    );

    const filteredTenants$ = observableCombineLatest([this.tenants$, this.tenantFilter$]).pipe(
      map(([tenants, filter]) => tenants.filter(tenant => this.filterTenant(tenant, filter)))
    );

    this.provisioningTenants$ = filteredTenants$.pipe(
      map(tenants => tenants.filter(tenant => tenant.status === 'provisioning'))
    );

    this.enabledTenants$ = filteredTenants$.pipe(
      map(tenants => tenants.filter(tenant => tenant.status === 'enabled'))
    );

    this.disabledTenants$ = filteredTenants$.pipe(
      map(tenants => tenants.filter(tenant => tenant.status === 'disabled'))
    );

    this.userDetails$ = store.pipe(select(fromShared.getUserDetailsData), filterUndefined());

    this.tenantListLoading$ = store.pipe(select(fromSharedData.isTenantsLoading));
    this.showCreateTenantWizard$ = store.pipe(select(fromTenants.isTenantCreationWizardShown));
  }

  onOpenDetailsClicked(tenant: TenantDetails): void {
    this.store.dispatch(new SelectTenantDetails(tenant));
    this.router.navigate(['/tenants', tenant.organizationId, 'activity']);
  }

  triggerTenantCreationWizard(): void {
    this.store.dispatch(new ShowTenantCreationWizard());
  }

  onFilterKeyUp(filter: string): void {
    this.tenantFilter$.next(filter);
  }

  canLogin(userDetails: UserDetails, tenantDetails: TenantDetails): boolean {
    return this.authService.canLoginToTenant(userDetails, tenantDetails);
  }

  loginToTenant(tenantDetails: TenantDetails): void {
    if (this.authService.requiresLoginReason(tenantDetails)) {
      const ref = this.matDialog.open(TenantLoginReasonPanelComponent);
      ref.componentInstance.saveClick.subscribe((auditDetails: AuditRequestReason) => {
        this.store.dispatch(new AuditedLoginToTenant(tenantDetails, undefined, auditDetails));
        ref.close();
      });
    } else {
      this.store.dispatch(new LoginToTenant(tenantDetails));
    }
  }

  private filterTenant(tenant: TenantDetails, filter: string): boolean {
    return (
      tenant.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
      tenant.organizationId.toLowerCase().indexOf(filter.toLowerCase()) !== -1
    );
  }
}
